<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { patchVoter, humanizeError, type VoterPatch } from '$lib/api';
    import type { VoterSummary } from '$lib/stores/voters';

    export let voter: Pick<VoterSummary, 'id' | 'first_name' | 'last_name' | 'phone' | 'email' | 'age' | 'do_not_contact'>;

    const dispatch = createEventDispatcher<{ saved: VoterSummary; cancel: void }>();

    let phone = voter.phone ?? '';
    let email = voter.email ?? '';
    let age: string = voter.age != null ? String(voter.age) : '';
    let dnc = voter.do_not_contact ?? false;
    let saving = false;
    let error: string | null = null;

    async function handleSave() {
        saving = true;
        error = null;
        const patch: VoterPatch = {};
        if (phone !== (voter.phone ?? '')) patch.phone = phone || null;
        if (email !== (voter.email ?? '')) patch.email = email || null;
        const parsedAge = age !== '' ? parseInt(age, 10) : null;
        if (parsedAge !== voter.age) patch.age = parsedAge;
        if (dnc !== voter.do_not_contact) patch.do_not_contact = dnc;
        try {
            const updated = await patchVoter(voter.id, patch);
            dispatch('saved', updated);
        } catch (e: any) {
            error = humanizeError(e, 'Could not save — please try again.');
        } finally {
            saving = false;
        }
    }

    function handleCancel() {
        dispatch('cancel');
    }
</script>

<div class="overlay" role="dialog" aria-modal="true" aria-label="Edit voter">
    <div class="modal">
        <h2>Edit — {voter.first_name} {voter.last_name}</h2>

        <label class="field">
            Phone
            <input type="tel" aria-label="Phone" bind:value={phone} placeholder="555-555-5555" />
        </label>

        <label class="field">
            Email
            <input type="email" aria-label="Email" bind:value={email} placeholder="volunteer@example.com" />
        </label>

        <label class="field">
            Age
            <input type="number" aria-label="Age" bind:value={age} min="18" max="120" />
        </label>

        <label class="dnc-row">
            <input type="checkbox" bind:checked={dnc} />
            Do not contact (DNC)
        </label>

        {#if error}
            <p class="error">{error}</p>
        {/if}

        <div class="actions">
            <button class="btn-secondary" on:click={handleCancel} disabled={saving}>Cancel</button>
            <button class="btn-primary" on:click={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
            </button>
        </div>
    </div>
</div>

<style>
    .overlay {
        position: fixed; inset: 0; background: rgba(0,0,0,.5);
        display: flex; align-items: center; justify-content: center; z-index: 1000;
    }
    .modal {
        background: white; border-radius: 12px; padding: 24px;
        width: min(420px, 95vw); display: flex; flex-direction: column; gap: 14px;
        z-index: 1001;
    }
    h2 { margin: 0; font-size: 1.1rem; }
    .field { display: flex; flex-direction: column; gap: 4px; font-size: .875rem; }
    .field input { padding: 10px; border: 1px solid #d1d5db; border-radius: 8px; }
    .dnc-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
    .actions { display: flex; gap: 8px; margin-top: 8px; }
    .btn-primary { flex: 1; padding: 12px; background: #1d4ed8; color: white; border: none; border-radius: 8px; cursor: pointer; }
    .btn-primary:disabled { opacity: 0.6; }
    .btn-secondary { flex: 1; padding: 12px; background: white; border: 1px solid #d1d5db; border-radius: 8px; cursor: pointer; }
    .error { color: #dc2626; font-size: .875rem; }
</style>
